import { Dispatch, SetStateAction, useMemo } from 'react';
import { FlexSpacer } from 'ui/flexSpacer';
import { useMissionControlContext } from 'modules/missionControl/contexts/MissionControlContext';
import { RecentCampaignAlertsSkeleton } from './RecentCampaignAlertsSkeleton';
import {
  RecentCampaignAlertsZeroState,
  RecentCampaignAlertsErrorState,
  RecentCampaignAlertsEmptyState,
} from './RecentCampaignAlertsPlaceholderStates';
import { RecentCampaignAlertsList } from './RecentCampaignAlertsList';
import { getAllProjectCampaignAlerts } from './RecentCampaignAlerts.utils';
import { useDevPanelContext } from '../../DevPanel/context/DevPanelContext';
import { RecentCampaignAlert } from 'modules/missionControl/types/health';

type RecentCampaignAlertsProps = {
  selectedAlert: RecentCampaignAlert | null;
  setSelectedAlert: Dispatch<SetStateAction<RecentCampaignAlert | null>>;
};

export const RecentCampaignAlertsContents = ({ setSelectedAlert }: RecentCampaignAlertsProps) => {
  const {
    projectCampaignAlertsQuery: { projectCampaignAlertsByMedium },
    recentCampaignAlertsQuery: {
      recentAlerts,
      isLoading: isLoadingRecentAlerts,
      isFetchingMore,
      hasError: hasRecentAlertsError,
      hasMore,
      fetchMore,
      refetch,
    },
  } = useMissionControlContext();

  const allProjectCampaignAlerts = useMemo(
    () => getAllProjectCampaignAlerts(projectCampaignAlertsByMedium || {}),
    [projectCampaignAlertsByMedium],
  );

  const isProjectCampaignAlertsConfigured = allProjectCampaignAlerts?.length > 0;

  const hasTriggeredAlerts = recentAlerts?.length > 0;
  const hasNoCampaignAlerts = isProjectCampaignAlertsConfigured && !hasTriggeredAlerts;
  const hasNoProjectAlerts = !isProjectCampaignAlertsConfigured && !hasTriggeredAlerts;

  const {
    devToolAlertsStates: {
      isDevToolLoadingStateEnabled,
      isDevToolErrorStateEnabled,
      isDevToolEmptyStateEnabled,
      isDevToolDataStateEnabled,
      isDevToolZeroStateEnabled,
    },
  } = useDevPanelContext();

  const isLoadingState = isDevToolLoadingStateEnabled || isLoadingRecentAlerts;
  const isErrorState = isDevToolErrorStateEnabled || hasRecentAlertsError;
  const isEmptyState = isDevToolEmptyStateEnabled || hasNoProjectAlerts;
  const isZeroState = isDevToolZeroStateEnabled || hasNoCampaignAlerts;
  const isDataState = isDevToolDataStateEnabled || hasTriggeredAlerts;

  /**
   * TODO: Work with design to add "No matching alerts" state if there are no alerts for the selected filters.
   */
  switch (true) {
    case isLoadingState:
      return <RecentCampaignAlertsSkeleton />;

    case isErrorState:
      return (
        <FlexSpacer justifyContent="center" alignItems="center" width="100%">
          <RecentCampaignAlertsErrorState refetch={refetch} />
        </FlexSpacer>
      );

    case isEmptyState:
      return (
        <FlexSpacer justifyContent="center" alignItems="center" width="100%">
          <RecentCampaignAlertsEmptyState />
        </FlexSpacer>
      );

    case isZeroState:
      return (
        <FlexSpacer justifyContent="center" alignItems="center" width="100%">
          <RecentCampaignAlertsZeroState />
        </FlexSpacer>
      );

    case isDataState:
      return (
        <RecentCampaignAlertsList
          recentAlerts={recentAlerts}
          setSelectedAlert={setSelectedAlert}
          hasMore={hasMore}
          fetchMore={fetchMore}
          isLoading={isLoadingRecentAlerts}
          isFetchingMore={isFetchingMore}
        />
      );

    default:
      return (
        <FlexSpacer justifyContent="center" alignItems="center" width="100%">
          <RecentCampaignAlertsErrorState refetch={refetch} />
        </FlexSpacer>
      );
  }
};
